import { ASnackbar } from '@adornis/adornis-components/a-snackbar';
import { AdornisRouter } from '@adornis/base/client/a-router';
import { logger } from '@adornis/base/logging';
import { EntityData } from '@adornis/baseql/entities/adornisEntity';
import { BehaviorController } from '@adornis/chemistry/controllers/behavior-controller';
import { DataLoadingController } from '@adornis/chemistry/controllers/data-loading-controller';
import { LocalStorageBehaviorController } from '@adornis/chemistry/controllers/local-storage-behavior-controller';
import { ObservableController } from '@adornis/chemistry/controllers/observable-controller';
import { ElementsDialog } from '@adornis/dialog/x-dialog';
import '@adornis/fonts/fonts';
import axios, { AxiosResponse } from 'axios';
import { shorten } from 'client/main-element';
import { EventLog } from 'db/EventLog';
import { Feedback } from 'db/Feedback';
import { MoodleAttempts } from 'db/MoodleAttempts';
import { goalDocs, mappingCourseGroupId } from 'db/staticData';
import { User } from 'db/User';
import { html, nothing, PropertyValueMap } from 'lit';
import { customElement } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { DateTime } from 'luxon';
import { Aggregator } from 'mingo';
import { combineLatestWith, map } from 'rxjs';

@customElement('feedback-pop')
export class FeedbackPop extends ElementsDialog<void> {
  // Dialog breaks if this function is missing!
  static get element_name() {
    return 'feedback-pop'; // -> This has to be the string given in @customElement
  }
  styles() {
    return {};
  }

  // Mapping from database titles to display titles in feedback dialog. Any label for which no mapping exists is not displayed in the "small" detail list (details toggled off).
  public detailMapping = {
    'Task Question': 'Frage',
    Response: 'Antwort',
    'Answer Date': 'Beantwortet am',
    'Link Taskview': 'Antwort ansehen',
    //'Link Accordeon': 'Antworten aller SuS',
    Score: 'Score',
    Nlp: 'Lernziele',
    //'Goal Id': 'Lernziel-Id',
    'Goal Title': 'Lernziel',
    // 'Goal Level': 'Lernziel-Level',
    'Goal Text': 'Lernziel-Beschreibung',
    'Task Ids': 'Aufgaben',
    // 'Essay Labels': 'Alle NLP-Scores',
  };
  public courseIdToTeacherIdMapping = {
    '377': '6',
    '375': '3230',
    '339': '2212',
    '340': '2263',
    '341': '2315',
    '342': '2367',
    '343': '3229',
    '344': '3230',
    '345': '3231',
    '346': '3232',
    '347': '3233',
    '348': '3234',
    '349': '3235',
    '350': '3236',
    '351': '3237',
    '352': '3238',
    '353': '3239',
    '354': '3240',
    '355': '3241',
    '356': '3242',
    '357': '3243',
    '358': '3244',
    '360': '3244',
    '364': '2367',
    '366': '2367',
    '367': '2263',
    '368': '2263',
    '372': '2263',
    '373': '2315',
    '109': '6',
    '103': '6',
  };
  @state() public toggleDetails = new BehaviorController(this, false);

  // The below are set via the "props" argument
  public mode: 'goal' | 'task' | 'perc' | 'percgoal' | null = null; // Mode "perc" is wrt the average task score; "percgoal" filter by a goal additionally
  public selectedUsers: Array<[string, string, string, string]> = []; // [[user._id, user.pseudoRealname, user.avgscore, user.userName]] For modes "goal" and "task"
  public selectedFullUsers: Array<[any]> = []; // [[user._id, user.pseudoRealname, user.avgscore, user.userName]] For modes "goal" and "task"
  public singleObjectiveId: string | null = null; // For modes "goal" and "task"
  public goalIdFilter: number | null = null; // For modes "task" and "perc"
  public percStart: number | null = null; // Only for mode "perc"
  public percEnd: number | null = null; // Only for mode "perc"
  public task: object | null = null; // task object
  public feedbackFromCheckbox: boolean | null = null; // true if feedback comes from checkbox selection

  // The below are regular
  public feedback = new BehaviorController<
    Array<{ token: string; text: string; icon: string; checked: Boolean; entry: string }>
  >(this, [
    { token: 'pos', text: 'Was lief gut?', icon: 'thumb_up', checked: false, entry: '' },
    { token: 'neg', text: 'Was lief nicht so gut?', icon: 'thumb_down', checked: false, entry: '' },
    { token: 'rec', text: 'Empfehlung', icon: 'lightbulb', checked: false, entry: '' },
    { token: 'else', text: 'Sonst noch etwas?', icon: 'reviews', checked: false, entry: '' },
  ]);

  @state() public courseSelected = new ObservableController(this, AdornisRouter.get().queryParams.get('course'));
  @state() public details = new BehaviorController(this, {});
  public rawAttempts = new DataLoadingController(this, () =>
    MoodleAttempts.subscribeAll<MoodleAttempts>()(MoodleAttempts.allFields),
  );
  @state() public users = new ObservableController(
    this,
    this.rawAttempts.observable.pipe(
      combineLatestWith(this.courseSelected.observable),
      map(([x, course]) =>
        x.filter(
          y => y.courseId === course && (!this.goalIdFilter ? true : y.goalIds.includes(Number(this.goalIdFilter))),
        ),
      ),
      map(raw => {
        const att = raw.map(att => att.toJSON()) as Array<EntityData<MoodleAttempts>>;
        const agg = new Aggregator([
          {
            $group: {
              _id: '$userId',
              userName: { $first: '$userName' },
              pseudoRealname: { $first: '$pseudoRealname' },
              avgscore: {
                $avg: {
                  $ifNull: ['$score', 0],
                },
              }, // { $avg: '$score' },
            },
          },
        ]).run(att) as Array<{
          _id: string;
          userName: string;
          pseudoRealname: string;
          avgscore: number;
          orderScore: number | null;
        }>;
        return agg.sort(function (x, y) {
          return Number(x._id) - Number(y._id);
        });
      }),
    ),
  );
  @state() public percUsers = new ObservableController(
    this,
    this.users.observable.pipe(
      combineLatestWith(this.updateComplete),
      map(([users, update]) => {
        if (this.percStart === null || !this.percEnd) return null;
        // await 0; // To ensure that everything is run through
        if (this.mode === 'percgoal' && this.selectedUsers) {
          return this.selectedUsers['users'].map(user => ({
            _id: user.userId,
            userName: user.userName,
            pseudoRealname: user.pseudoRealname,
            avgscore: user.score,
          }));
        }
        return users.filter(u => u.avgscore >= this.percStart! && u.avgscore <= this.percEnd!);
      }),
    ),
  );
  @state() public displayUsers = new ObservableController(
    this,
    this.percUsers.observable.pipe(
      map(percusers => {
        if (!!percusers?.length && !!this.selectedUsers.length) return null;
        if (!!percusers?.length) return percusers;
        else
          return this.selectedUsers.map(u => ({
            _id: u[0],
            userName: u[3],
            pseudoRealname: u[1],
            avgscore: Number(u[2]),
            orderScore: null,
          }));
      }),
    ),
  );
  @state() public realnames = new DataLoadingController(this, () => User.subscribeAll<User>()(User.allFields));
  @state() public usernameMapping = new LocalStorageBehaviorController(this, 'usernameMapping', [] as string[][]);

  protected firstUpdated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    // if (this.percStart != null && this.percEnd != null) this.mode = 'perc';

    console.log(
      this.percUsers.value,
      'this.mode',
      this.mode,
      'this.percEnd',
      this.percEnd,
      'this.percStart',
      this.percStart,
      '#################',
      goalDocs,
      this.courseSelected.value,
      goalDocs.find(g => this.details.value['Course Id'] == this.courseSelected.value && g.goalNumber == '3'),
    );
    // console.log('CONDITIOBS', this.mode, this.details.value, this.percStart, this.percEnd);
    // console.log(
    //   'RES----------------------------------------',
    //   this.mode === 'perc' && this.percStart! <= 0.5 && this.percEnd! <= 0.5,
    // );

    this.details.next(
      Object.fromEntries(
        Object.entries(this)
          .filter(([k, v]) => k.startsWith('popdata'))
          .map(([i, v]) => [
            String(i)
              .replace('popdata', '')
              .replace(/([A-Z])/g, match => ` ${match}`)
              .replace(/^./, match => match.toUpperCase())
              .trim(),
            v,
          ]),
      ),
    );
    if (
      this.selectedUsers.length === 1 ||
      (Number(this.percStart) <= 0.5 && Number(this.percEnd) <= 0.5) ||
      (Number(this.percStart) > 0.5 && Number(this.percEnd) > 0.5)
    ) {
      this.feedback.next([
        {
          token: 'pos',

          text: 'Was lief gut?',
          icon: 'thumb_up',
          checked:
            (this.details.value['Score'] != null && Number(this.details.value['Score']) > 0.5) ||
            (this.mode === 'perc' && this.percStart! > 0.5 && this.percEnd! > 0.5),
          entry:
            // this.details.value['Score'] != null ?
            this.mode === 'goal' && Number(this.details.value['Score']) > 0.5
              ? `Gut gemacht. Du hast beim Lernziel "${
                  this.details.value['Goal Title']
                }" Fortschritte gemacht. Dein durchschnittlicher Score bei diesem Lernziel ist ${(
                  Number(this.details.value['Score']) * 100
                ).toFixed()}%.`
              : this.mode === 'goal' &&
                Number(this.details.value['Score']) <= 0.5 &&
                this.details.value['Best Goal']['score'] >= 0.5
              ? `Es ist schon mal gut, dass du das Lernziel "${this.details.value['Best Goal']['goalTitle']}" mit ${(
                  Number(this.details.value['Best Goal']['score']) * 100
                ).toFixed()}% gemeistert hast, aber...`
              : this.mode === 'task' && Number(this.details.value['Score'] > 0.5)
              ? `Es ist gut, dass du die Aufgabe ${this.details.value['Task Id']}(${
                  this.details.value['Quiz Name']
                }) (vollständig oder teilweise) gemeistert hast. (Score: ${(
                  Number(this.details.value['Score']) * 100
                ).toFixed()})%`
              : this.mode === 'perc' && this.percStart! > 0.5 && this.percEnd! > 0.5 //positiv bei Main Dashbaord Histogramm inklusive Slider (dh es ist variable. ): perc: es werden 2 quantile weitergegeben. zb main histogram die zwischen 10 und 20% sind..
              ? `Du kommst gut voran! Dein durchschnittlicher Score liegt zwischen ${(
                  Number(this.percStart) * 100
                ).toFixed()}% und ${(Number(this.percEnd) * 100).toFixed()}%.`
              : this.mode === 'percgoal' && this.percStart! >= 0.5 && this.percEnd! > 0.5 //positiv bei Goal-Histogram: wenn Goal
              ? `Du kommst gut voran mit dem Lernziel "${
                  this.details.value['Goal Title']
                }"! Dein durchschnittlicher Score für das Lernziel ${this.goalIdFilter} liegt zwischen ${(
                  Number(this.percStart) * 100
                ).toFixed()}% und ${(Number(this.percEnd) * 100).toFixed()}%.`
              : '',
          // : '',
        },

        {
          token: 'neg',
          text: 'Was lief nicht so gut?',
          icon: 'thumb_down',
          checked:
            (this.details.value['Score'] != null && Number(this.details.value['Score']) <= 0.5) ||
            this.details.value['Score'] === null ||
            Number(this.details.value['Score']) <= 0.5 ||
            (this.mode === 'perc' && this.percStart! <= 0.5 && this.percEnd! <= 0.5),
          entry:
            // this.details.value['Score'] != null ?
            this.mode === 'goal' && Number(this.details.value['Score']) < 0.5
              ? `Ich sehe, du hast Schwierigkeiten mit dem Lernziel "${
                  this.details.value['Goal Title'] ? this.details.value['Goal Title'] : this.task['goalTitle']
                }". Dein durchschnittlicher Score bei diesem Lernziel ist ${(
                  Number(this.details.value['Score']) * 100
                ).toFixed()}%.`
              : this.mode === 'task' && Number(this.details.value['Score'] <= 0.5)
              ? `Du scheinst Schwierigkeiten mit Aufgabe ${this.details.value['Moodle Task Id']} (${
                  this.details.value['Quiz Name']
                }) zu haben (Score: ${(Number(this.details.value['Score']) * 100).toFixed()}%).`
              : this.mode === 'perc' && this.percStart! <= 0.5 && this.percEnd! <= 0.5 //positiv bei Main Dashbaord Histogramm inklusive Slider (dh es ist variable. ): perc: es werden 2 quantile weitergegeben. zb main histogram die zwischen 10 und 20% sind..
              ? `Du scheinst allgemein Schwierigkeiten mit den Themen zu haben. Dein Score liegt zwischen ${(
                  Number(this.percStart) * 100
                ).toFixed()}% und ${(Number(this.percEnd) * 100).toFixed()}%.`
              : this.mode === 'percgoal' && this.percStart! < 0.5 && this.percEnd! < 0.5 //negativ bei Goal-Histogram: wenn Goal
              ? `Ich sehe, du hast Schwierigkeiten mit dem Lernziel "${
                  this.details.value['Goal Title']
                }". Dein Score zu diesem Lernziel liegt zwischen ${(Number(this.percStart) * 100).toFixed()}% und ${(
                  Number(this.percEnd) * 100
                ).toFixed()}%.`
              : '',
          // : '',
        },
        {
          token: 'rec',
          text: 'Empfehlung',
          icon: 'lightbulb',
          checked: false,
          entry:
            this.mode === 'task' && Number(this.details.value['Score'] <= 0.5)
              ? `Aufgabe ${this.details.value['Moodle Task Id']} (${
                  this.details.value['Quiz Name']
                }) einsehen: https://lpa.edutec.science/mod/quiz/view.php?id=${
                  this.details.value['Link Accordeon'].split('report.php?id=')[1].split('&')[0]
                }`
              : '',
        },
        { token: 'else', text: 'Sonst noch etwas?', icon: 'reviews', checked: false, entry: '' },
      ]);
    }
  }
  public render() {
    console.log('USERS', this.users.value, this.percUsers.value, this.selectedUsers, this.displayUsers.value);
    // console.log('percStart, percEnd', this.percStart, this.percEnd);
    // console.log('this.goalIdFilter', this.goalIdFilter);
    // console.log('this.mode', this.mode);
    // console.log('FEEDBACK', this.feedback.value);
    console.log('Task', this.task);
    console.log('DETAILS', this.details.value);
    // console.log('MODE', this.mode);
    // console.log('perc', this.percStart, this.percEnd);
    // console.log('ATTEMPTS', this.rawAttempts.value);
    if (this.displayUsers.value === null)
      return html`<x-text style="padding:20px;">
        Feedback dialog is set up incorrectly, passed either both a percentile interval and a list of users or neither
        of both!
      </x-text>`;
    if (!this.selectedUsers?.length && !this.percUsers.value?.length)
      return html`<x-text style="padding:20px;">No students selected!</x-text>`;
    return html` <x-flex center crossaxis-center space="xl" style="padding:30px; width: 50vw; min-width: 350px;">
      <!-- DETAILS -->
      <x-flex center crossaxis-center space="md" style="width:100%;">
        <x-h1>Feedback an Auswahl senden</x-h1>

        <x-flex space="sm" crossaxis-center>
          ${this.goalIdFilter
            ? html`<x-text>Gefiltert nach Lernziel ${this.details.value['Goal Title']} </x-text>`
            : nothing}
          ${this.percStart !== undefined &&
          this.percEnd !== undefined &&
          this.percStart !== null &&
          this.percEnd !== null
            ? html`<x-text>
                <h3>
                  SuS mit durchschnittlichem Gesamt-Score von ${(this.percStart || 0) * 100}% bis
                  ${((this.percEnd || 0) * 100).toFixed()}%
                </h3>
              </x-text>`
            : nothing}
        </x-flex>

        ${console.log('this.details.valuethis.details.valuethis.details.valuethis.details.value')}
        ${console.log(this.details.value)} ${console.log('this.details. LENG:')}
        ${console.log(Object.keys(this.details.value).length)}
        ${Object.keys(this.details.value).length && Object.keys(this.details.value).length !== 4 //when click on grey of
          ? html`<x-grid columns="2" style="grid-template-columns:auto auto; grid-gap:15px;">
              ${Object.entries(this.details.value).map(([i, v]) =>
                Object.keys(this.detailMapping).includes(i) || this.toggleDetails.value
                  ? html`<x-text style="text-align:right;  white-space:text-wrap;">
                        ${Object.keys(this.detailMapping).includes(i) ? this.detailMapping[i] : i}:
                      </x-text>
                      ${typeof v === 'string' && v.startsWith('http')
                        ? html`<x-link href=${v}>
                            <x-text style="color:blue; overflow-wrap: anywhere;">${v}</x-text>
                          </x-link>`
                        : html` <x-text style="overflow-wrap: anywhere;">
                            ${i == 'Task Ids'
                              ? AdornisRouter.get('page').value != 'goal-hist'
                                ? html`<x-accordeon
                                    .items=${(v as string[]).map(x => {
                                      const found = this.rawAttempts.value?.find(
                                        y => y.taskId == x && y.userId == this.details.value['User Id'],
                                      );
                                      return {
                                        title:
                                          found?.taskQuestion.slice(0, 50) +
                                          (found?.taskQuestion.length || 0 > 10 ? '...' : ''),
                                        text: html`<x-flex space="sm">
                                          <x-text>Question: ${found?.taskQuestion}</x-text>
                                          <x-text>Response: ${found?.response}</x-text
                                          ><x-text>Score: ${found?.score}</x-text>
                                          <x-link href=${ifDefined(found?.linkTaskview)}>
                                            Moodle Link: ${found?.linkTaskview}
                                          </x-link>
                                          <x-link href=${ifDefined(found?.linkTaskview)}>
                                            Fragetyp: ${found?.taskType}
                                          </x-link>
                                        </x-flex>`,
                                      };
                                    })}
                                  ></x-accordeon>`
                                : ''
                              : i === 'Score'
                              ? [undefined, null].includes(v)
                                ? ''
                                : `${(Number(v) * 100).toFixed()}%`
                              : i == 'Best Goal'
                              ? JSON.stringify(v, null, 2)
                              : (i === 'Essay Labels' || i === 'Nlp') && Array.isArray(v)
                              ? v.map(
                                  rating =>
                                    html` ${goalDocs.find(
                                        g =>
                                          (mappingCourseGroupId.Solarzellen.includes(this.courseSelected.value!)
                                            ? 'Solarzellen'
                                            : 'Laptop') == g.courseGroup && g.goalNumber == rating.goalId,
                                      )?.['goalTitle']}:
                                      ${(Number(rating.nlp) * 100).toFixed()}% <br />`,
                                )
                              : v}
                          </x-text>`}`
                  : nothing,
              )}
            </x-grid>`
          : this.task
          ? this.task['goalTitle']
            ? html`<x-grid columns="2" style="grid-template-columns:auto auto; grid-gap:15px;"
          ><x-text style="text-align:right; white-space:text-wrap;" ></x-text> <x-text  style="overflow-wrap: anywhere;">Schüler*in hat keine Aufgaben zu diesem Lernziel bearbeitet.</x-text>
          <x-text style="text-align:right; white-space:text-wrap;">Lernziel:</x-text> <x-text  style="overflow-wrap: anywhere;"> ${
            this.task['goalTitle']
          }</x-text>
          <x-text style="text-align:right; white-space:text-wrap;">Lernziel-Beschreibung</x-text></x-text> <x-text  style=" overflow-wrap: anywhere;"> ${shorten(
            this.task['goalText'],
            400,
          )}</x-text>
        </x-grid>`
            : this.task['taskQuestion']
            ? html`<x-grid columns="2" style="grid-template-columns:auto auto; grid-gap:15px;"
          ><x-text style="text-align:right; white-space:text-wrap;" >Antwort:</x-text> <x-text  style="overflow-wrap: anywhere;">Die Aufgabe wurde nicht bearbeitet</x-text>
          <x-text style="text-align:right; white-space:text-wrap;">Quiz:</x-text> <x-text  style="overflow-wrap: anywhere;"> ${
            this.task['quizName']
          }</x-text>
          <x-text style="text-align:right; white-space:text-wrap;">Aufgabe:</x-text></x-text> <x-text  style=" overflow-wrap: anywhere;"> ${shorten(
            this.task['taskQuestion'],
            400,
          )}</x-text>
          <x-text style="text-align:right; white-space:text-wrap;">Aufgabentyp:</x-text> </x-text> <x-text  style="overflow-wrap: anywhere;"> ${
            this.task['taskType']
          }</x-text>
        </x-grid>`
            : nothing
          : nothing}

        <x-grid
          style=" text-align:center; grid-template-columns: repeat(${this.percEnd ? 2 : 2}, auto); grid-gap:15px;"
        >
          <!-- <x-text style="text-decoration: underline;">Student ID</x-text> -->
          <x-text style="text-decoration: underline; text-align:center;">Auswahl</x-text>
          ${this.mode == 'goal'
            ? html`<x-text style="text-decoration: underline;text-align:center;"
                >Durchschnitt über alle Lernziele</x-text
              >`
            : this.mode == 'perc' || this.mode == 'task'
            ? html`<x-text style="text-decoration: underline;text-align:center;"
                >Durchschnitt über alle Aufgaben</x-text
              >`
            : this.mode == 'percgoal'
            ? html`<x-text style="text-decoration: underline;text-align:center;"
                >Durchschnitt im ausgewählten Lernziel</x-text
              >`
            : ''}
          ${console.log('this.displayUsers', this.displayUsers.value)}
          ${console.log('this.selectedusers', this.selectedUsers)}
          ${console.log(
            'this.selectedFullUsersselectedFullUsersselectedFullUsersselectedFullUsers',
            this.selectedFullUsers,
          )}
          ${this.feedbackFromCheckbox
            ? this.selectedFullUsers.map(su => {
                console.log('suiuiuiuiuiuiuiuiuiuiuiuiuiuiuiuiui');
                console.log(su);
                console.log('realnamesrealnamesrealnamesrealnamesrealnames');
                console.log(this.realnames.value);
                return html`
                  <x-text>
                    ${shorten(
                      this.usernameMapping.value?.find(x => x[0] === su[0].userName)?.[2] || su[0].pseudoRealname,
                    )}
                  </x-text>
                  <x-text>${(su[0].avgscore * 100).toFixed()}%</x-text>
                `;
              })
            : this.displayUsers.value?.map(u => {
                //this.selectedUsers.map(u => {
                // console.log('selectedUsers: ', this.selectedUsers);
                //console.log('User: ', u);
                // console.log(this.realnames.value?.find(x => x._id === u.userName)?.realName);
                return html` <!-- <x-text>${u._id}</x-text> -->
                  <x-text>
                    ${shorten(this.usernameMapping.value?.find(x => x[0] === u.userName)?.[2] || u.pseudoRealname)}
                  </x-text>
                  ${this.percEnd
                    ? html`<x-text>${(u.avgscore * 100).toFixed()}%</x-text>`
                    : html`<x-text>${(u.avgscore * 100).toFixed()}%</x-text>`}`;
              })}
        </x-grid>

        <x-flex horizontal style="width:100%;" space="md">
          ${this.details.value['Task Id'] || this.details.value['Goal Id']
            ? html` <x-button
                mode="outline"
                style="flex:1; display:none"
                @click=${() => this.toggleDetails.next(!this.toggleDetails.value)}
              >
                Toggle details
              </x-button>`
            : html`
                <x-button
                  style="flex:1;"
                  mode="outline"
                  @click=${() =>
                    AdornisRouter.goTo(
                      'task-view',
                      new URLSearchParams({
                        course: this.courseSelected.value,
                        sort: Number(this.percEnd) < 0.5 && Number(this.percStart) < 0.5 ? 'asc' : 'desc',
                        users: this.displayUsers.value?.map(u => u._id).join(','),
                      } as Record<string, string>),
                    )}
                >
                  Leistungen im Detail
                </x-button>
                <!-- <x-button
                  mode="outline "
                  style="flex:1; visibility:hidden;"
                  @click=${() => this.toggleDetails.next(!this.toggleDetails.value)}
                >
                  Toggle details
                </x-button> -->
              `}
        </x-flex>
      </x-flex>

      <!-- FEEDBACK -->
      <!-- Feedback section -->
      <x-flex center crossaxis-center space="md" style="width:100%;">
        <x-h1>Feedback</x-h1>
        ${this.feedback.value.map(
          (f, i) => html` <x-flex horizontal space="sm">
              <x-icon type="material-outlined">${f.icon}</x-icon><x-text>${f.text}</x-text>
            </x-flex>
            <x-flex horizontal crossaxis-center style="width:100%;">
              <mwc-checkbox
                ?checked="${!!f.checked}"
                style="--mdc-theme-secondary: ${this.colors.accent}"
                @click="${() => {
                  const changed = this.feedback.value;
                  changed[i]!.checked = !f.checked;
                  this.feedback.next(changed);
                }}"
              ></mwc-checkbox>
              <textarea
                style="width:100%; height:95%; margin:5px; padding:20px; border: 1px gray solid;"
                @input="${e => {
                  console.log(e.target.value, 'textarea');
                  const changed = this.feedback.value;
                  changed[i]!.entry = e.target.value;
                  changed[i]!.checked = !!e.target.value;
                  this.feedback.next(changed);
                }}"
              >
${f.entry}
    </textarea
              >
            </x-flex>`,
        )}
      </x-flex>

      <!-- Buttons -->
      <x-flex horizontal crossaxis-center style="width:100%" space-around>
        <x-button mode="outline" @click="${() => this.cancel()}"> Abbrechen </x-button>
        <x-button
          @click="${() => this.close()}"
          ?disabled="${!this.feedback.value.reduce((a, c) => a + Number(!!c.checked) * Number(!!c.entry), 0)}"
        >
          Feedback senden
        </x-button>
      </x-flex>
    </x-flex>`;
  }

  // Default closing function
  public async close() {
    // add your logic here if needed
    super.close();
    // this.displayUsers.value?.forEach(u => {
    //   this.feedback.value.forEach(f => {
    //     if (!f.checked) return;
    //     new Feedback({
    //       userId: u[0],
    //       userName: u[1],
    //       pseudoRealname: u[1],
    //       timestamp: DateTime.now(),
    //       mode: this.mode,
    //       singleObjectiveId: this.singleObjectiveId, // Only relevant for feedback coming from the table
    //       type: f.token,
    //       entry: f.entry,
    //       course: this.courseSelected.value,
    //       users: this.displayUsers.value,
    //       rest: this.feedback // ToDO add this.courseSelected.value this.feedback Ids + Score + percstart, percend.
    //     }).create();
    //   });
    // });

    ElementsDialog.confirm(
      html`<x-flex space="md" margin="md">
        <x-h2 style="text-align:center;">Feedback für SuS in ähnlichen Situationen</x-h2>
        <br /><x-text
          >Das Feedback wurde versendet! <br />
          ${this.displayUsers.value?.length} Schüler*innen haben folgendes Feedback erhalten:</x-text
        >
        <x-grid style="grid-template-columns: 15px auto auto; grid-gap: 5px;">
          ${this.feedback.value
            .filter(x => x.checked)
            .map(
              x =>
                html`<x-icon>check</x-icon> <x-text>${x.text}: </x-text
                  ><x-text style="font-style:italic;">${x.entry}</x-text>`,
            )}
        </x-grid>
        ${console.log(this.feedback)}
        <br /><x-text>
          <b
            >Möchtest du in Zukunft daran erinnert werden, das gleiche Feedback an SuS in ähnlichen Situationen zu
            senden?</b
          >
          <br /><br />
          Mit der Erinnerung hast du immer noch die Möglichkeit, Dein Feedback nachher anzupassen.</x-text
        >
      </x-flex>`,
      {
        acceptText: 'Erinnern!',
        declineText: 'Schließen',
      },
    ).then(x => {
      new Feedback({
        course: this.courseSelected.value,
        timestamp: DateTime.now(),
        recipients: this.displayUsers.value?.map(x => JSON.stringify({ userid: x._id, score: x.avgscore })),
        intervalStart: this.percStart,
        intervalEnd: this.percEnd,
        posFb: this.feedback.value[0]?.entry,
        negFb: this.feedback.value[1]?.entry,
        recFb: this.feedback.value[2]?.entry,
        elseFb: this.feedback.value[3]?.entry,
        goal: this.details.value['Goal Id'],
        task: this.details.value['Task Id'],
        automate: x,
        url: '/' + AdornisRouter.get('page').value + '?' + AdornisRouter.get().queryParams.getValue(),
        mode: this.mode,
      }).create();
      ASnackbar.show('Die automatische Erinnerung wurde noch nicht implementiert.');
    });
    new EventLog({
      time: DateTime.now(),
      event: 'feedback',
      info: this.displayUsers.value?.map(u => u._id).join(', '),
    }).create();
    // ###########################################################################
    // MOODLE FEEDBACK INTERFACE HERE: Metadata is available in this.details.value
    // ###########################################################################
    // Das Beispiel ab hier ist der POST auf die NLP API, dh URL austauschen und als zweites Argument einen Array von JSONs. Response als var vor dem Try definieren damit's außerhalb vom try-catch auch noch verfügbar ist.
    var response: AxiosResponse;
    //https://lpa.edutec.science/local/api/index.php?action=message&authtoken=716ef91efd9e8c59a5293caad53c3bf1

    try {
      this.displayUsers.value?.forEach(u => {
        console.log(u);
      });

      let feedbackPostArray: object[] = [];

      this.displayUsers.value?.forEach(u => {
        console.log(u);
        let fbobj = {
          id: u._id,
          from: this.courseIdToTeacherIdMapping[this.courseSelected.value],
          feedback:
            this.feedback.value[0]?.entry +
            '\n' +
            this.feedback.value[1]?.entry +
            '\n \n' +
            this.feedback.value[2]?.entry +
            '\n' +
            this.feedback.value[3]?.entry +
            '\n \n' +
            '**Fandest du das Feedback hilfreich?** Bitte gib diesem Feedback eine Schulnote und begründe deine Benotung. 1 (= sehr gut) bis 5 (= mangelhaft)',
        };
        feedbackPostArray.push(fbobj);
      });
      console.log('uuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuuu');
      console.log(feedbackPostArray);

      response = await axios.post(
        'https://lpa.edutec.science/local/api/index.php?action=message&authtoken=716ef91efd9e8c59a5293caad53c3bf1',
        feedbackPostArray,
      );
      logger.info({ resp: response.data }, 'adsf');
    } catch (e) {
      logger.info({ error: e }, 'asdf');
    }
    // Ende Beispiel
  }
  public async cancel(returnVal?: any): Promise<void> {
    super.cancel();
    console.log('CANCEEEEEEEEEEEEEEELING', this.displayUsers.value?.map(u => u._id).join(', '));
    new EventLog({
      time: DateTime.now(),
      event: 'feedbackCancel',
      info: this.displayUsers.value?.map(u => u._id).join(', '),
    }).create();
  }

  public getInnerWrapper(options: DialogOptions) {
    const wrapperElem = super.getInnerWrapper(options);
    // wrapperElem.style.setProperty('max-width', '60vw');
    // wrapperElem.style.setProperty('max-height', '60vh');
    // wrapperElem.style.setProperty('min-width', '60vw');
    // wrapperElem.style.setProperty('min-height', '60vh');
    return wrapperElem;
  }
}
